/**
 * Pending-update state (SU spec requirements 2 and 3). See docs/code/lib.md for
 * the #5277 incident this design replaced.
 *
 * The controllerchange signal only sets a PENDING flag. Nothing user-visible
 * fires — no prompt, no toast, no banner. The swap happens at a boundary the user
 * already perceives as a transition (the next route navigation, or returning to a
 * backgrounded tab); that wiring lives outside this module and drives it through
 * `applyUpdateNow()`. The old page stays usable in the meantime because the
 * service worker retains the outgoing build's chunks (`src/sw.ts`).
 *
 * The one exception is the force path: if the running build is below the
 * configured `MIN_SUPPORTED_VERSION`, this module reloads immediately without
 * waiting for a boundary.
 *
 * PUBLIC SURFACE IS FROZEN at the three exports below (the SU program's pinned
 * interface). Everything else here — the listeners, the polling, the version
 * comparison, the force path — is internal.
 */

import { fetchMinSupportedVersion } from '@/config';
import { compareDottedVersions, runningAppVersion, runningBuildId } from '@/lib/appVersion';
import { whenDurableWritesSettled } from '@/lib/durableWriteBarrier';
import { reportError } from '@/lib/errors/reportError';

/**
 * How long a swap waits for an in-flight IndexedDB journal write to settle
 * (spec requirement 4). Long enough for a `put` on a healthy connection by
 * orders of magnitude; short enough that a wedged connection cannot pin a user
 * on a build we have declared unsafe. A timeout is reported, not swallowed.
 */
const JOURNAL_HANDOFF_TIMEOUT_MS = 3_000;

/**
 * How long to wait for a claiming worker to report its build id. Generous for a
 * postMessage round-trip to an already-running worker; short enough that a silent
 * one resolves promptly to "treat as a replacement".
 */
const BUILD_ID_QUERY_TIMEOUT_MS = 2_000;

/**
 * How often an open tab re-reads MIN_SUPPORTED_VERSION from `/config.json`.
 *
 * This interval IS the incident response time, which is why it is short.
 * Flipping MIN_SUPPORTED_VERSION does not ship a new build — it is a ConfigMap
 * value that `docker-entrypoint.sh` renders into `/config.json` at container
 * start, so the image and therefore the service worker are unchanged. There is no
 * new SW, no `controllerchange`, no update signal of any kind. Polling is the
 * ONLY detection path, so however long this is, that is how long a tab can keep
 * running a build we have declared unsafe.
 *
 * See docs/code/lib.md for the interval's history and cost justification.
 */
const MIN_VERSION_POLL_INTERVAL_MS = 60 * 1_000;

/**
 * Floor between visibility-triggered re-checks — deliberately DECOUPLED from the
 * steady-state interval above. Returning to a tab is the cheapest high-value
 * moment to re-check: the user is about to interact, and it costs one request.
 * This floor exists only so rapid tab-switching cannot hammer the endpoint.
 *
 * See docs/code/lib.md for why this is decoupled rather than throttled at the
 * steady-state interval.
 */
const VISIBILITY_RECHECK_FLOOR_MS = 15 * 1_000;

/**
 * Ceiling on forced reloads per tab session.
 *
 * MIN_SUPPORTED_VERSION set above the newest deployed build would otherwise
 * reload-loop every tab forever, turning a mistyped ConfigMap value into an
 * outage. Three attempts is enough to ride out a rollout still converging;
 * beyond that the misconfiguration is reported and the tab stops trying.
 *
 * See docs/code/lib.md for why shortening the poll interval made this ceiling
 * more load-bearing.
 */
const MAX_FORCED_RELOADS_PER_SESSION = 3;
const FORCED_RELOAD_COUNT_KEY = 'su.forcedReloads';

export type UpdateApplyReason = 'navigation' | 'visibility' | 'forced';

let updatePending = false;
let applying = false;
let forcedReloadCeilingReported = false;
const subscribers = new Set<() => void>();

// ---------------------------------------------------------------------------
// Public surface (pinned)
// ---------------------------------------------------------------------------

/** True once a newer build has taken over the service worker for this page. */
export function isUpdatePending(): boolean {
  return updatePending;
}

/**
 * Observe the pending flag. Fires when it flips false → true; it never flips
 * back, so at most one call per page. Does NOT fire on subscribe — pair it with
 * `isUpdatePending()` to catch an update that landed before you subscribed.
 */
export function subscribeUpdatePending(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Swap the document onto the new build: a full document load of the CURRENT
 * `window.location`, so a caller applying at a navigation boundary must let the
 * destination URL become current first.
 *
 * Waits for any in-flight durable write to settle before navigating so a
 * journaled edit is never dropped mid-`put` (spec requirement 4). Idempotent —
 * repeat calls while a swap is already in progress are ignored.
 */
export function applyUpdateNow(reason: UpdateApplyReason): void {
  if (applying) return;
  applying = true;
  if (reason === 'forced' && !recordForcedReload()) {
    applying = false;
    return;
  }
  void swapDocument(reason);
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

async function swapDocument(reason: UpdateApplyReason): Promise<void> {
  await whenDurableWritesSettled(JOURNAL_HANDOFF_TIMEOUT_MS);
  console.log(`[Update] Applying pending update (${reason})`);
  window.location.reload();
}

function markUpdatePending(): void {
  if (updatePending) return;
  updatePending = true;
  for (const cb of [...subscribers]) {
    cb();
  }
}

/**
 * Bump and check the per-session forced-reload counter. Returns false once the
 * ceiling is hit, so a MIN_SUPPORTED_VERSION above every deployed build reports
 * itself instead of looping.
 *
 * `sessionStorage` is the right store: it survives the reload we are about to
 * perform and dies with the tab, which is exactly the scope of the loop.
 */
function recordForcedReload(): boolean {
  let previous = 0;
  try {
    const raw = sessionStorage.getItem(FORCED_RELOAD_COUNT_KEY);
    if (raw !== null) {
      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed)) previous = parsed;
    }
  } catch (err) {
    // Storage disabled (private mode, blocked cookies). The loop guard is a
    // safety net over the force path, not the force path itself — losing it must
    // not disable the incident lever, but it does need to be visible.
    reportError(err, 'updateLifecycle.forcedReloadCounter', { level: 'warning' });
    return true;
  }

  if (previous >= MAX_FORCED_RELOADS_PER_SESSION) {
    // Report ONCE per page load, not once per poll. At a 60s interval an
    // unsatisfiable MIN_SUPPORTED_VERSION would otherwise emit an error-level
    // report every minute from every open tab — a GlitchTip flood during exactly
    // the incident the operator is trying to read.
    if (!forcedReloadCeilingReported) {
      forcedReloadCeilingReported = true;
      reportError(
        new Error(
          `MIN_SUPPORTED_VERSION forced ${previous} reloads without reaching a supported ` +
            `build (running ${runningAppVersion()}) — check the configured value`,
        ),
        'updateLifecycle.forcedReloadLoop',
        { level: 'error', extra: { runningVersion: runningAppVersion(), attempts: previous } },
      );
    }
    return false;
  }

  sessionStorage.setItem(FORCED_RELOAD_COUNT_KEY, String(previous + 1));
  return true;
}

/**
 * Force-reload when the running build is below `minSupportedVersion`.
 *
 * An uncomparable pair (a non-dotted running version, or a malformed ConfigMap
 * value) does NOT force: the check is off and the reason is reported, rather than
 * either silently ignoring a live incident lever or reloading every tab on a
 * typo.
 */
function enforceMinSupportedVersion(minSupportedVersion: string | null): void {
  if (minSupportedVersion === null) return;

  const running = runningAppVersion();
  const ordering = compareDottedVersions(running, minSupportedVersion);
  if (ordering === null) {
    reportError(
      new Error(
        `MIN_SUPPORTED_VERSION is set (${minSupportedVersion}) but cannot be compared to ` +
          `the running version (${running}) — the force path is inactive`,
      ),
      'updateLifecycle.minSupportedVersion',
      { level: 'warning', extra: { runningVersion: running, minSupportedVersion } },
    );
    return;
  }
  if (ordering >= 0) return;

  console.warn(
    `[Update] Running build ${running} is below MIN_SUPPORTED_VERSION ` +
      `${minSupportedVersion} — reloading now`,
  );
  applyUpdateNow('forced');
}

function startMinSupportedVersionPolling(): void {
  let lastPoll = 0;

  const poll = () => {
    lastPoll = Date.now();
    // EXPECTED_DOMAIN_ERROR — a failed /config.json poll is a network condition,
    // not a defect, and the next poll retries. Reported at `info` (the same
    // shape swRegistration uses for its update poll) so an environment where the
    // poll NEVER succeeds is observable rather than silently un-levered.
    fetchMinSupportedVersion()
      .then(enforceMinSupportedVersion)
      .catch((err) => {
        reportError(err, 'updateLifecycle.minSupportedVersionPoll', { level: 'info' });
      });
  };

  // Poll straight away, not on the first interval tick: a tab loading DURING an
  // incident must not sit on the unsafe build for a whole poll period, and the
  // boot-time config memo is no help — /config.json may well have been read
  // before the ConfigMap flip.
  poll();
  setInterval(poll, MIN_VERSION_POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // Throttled by its OWN floor, not the steady-state interval — see
    // VISIBILITY_RECHECK_FLOOR_MS. Gating this behind the long interval made
    // returning to a tab useless inside the polling window.
    if (Date.now() - lastPoll < VISIBILITY_RECHECK_FLOOR_MS) return;
    poll();
  });
}

/**
 * Ask the worker currently controlling this page which build it is.
 *
 * Resolves `null` when there is no controller, or when it does not answer within
 * the timeout — a worker predating this message, or one already replaced.
 */
function controllerBuildId(): Promise<string | null> {
  const controller = navigator.serviceWorker.controller;
  if (!controller) return Promise.resolve(null);

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), BUILD_ID_QUERY_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent) => {
      clearTimeout(timer);
      // Boundary: this crosses a postMessage, so the payload is genuinely unknown
      // and may be null. Validate rather than assert a shape onto it.
      const data = event.data as { buildId?: unknown } | null | undefined;
      const buildId = data?.buildId;
      resolve(typeof buildId === 'string' ? buildId : null);
    };
    controller.postMessage({ type: 'GET_BUILD_ID' }, [channel.port2]);
  });
}

/**
 * Decide whether the worker that just claimed this page represents a NEW BUILD.
 *
 * Compares BUILDS, not controller history. See docs/code/lib.md for the #5277
 * regression this replaced and the two look-alike scenarios.
 *
 * Silence from the worker counts as a replacement: a false positive costs one
 * full document load at a boundary the user already reads as a transition, while
 * a false negative strands the tab with `isUpdatePending()` false forever.
 */
async function onControllerChange(): Promise<void> {
  if (updatePending) return;
  const claiming = await controllerBuildId();
  if (claiming === runningBuildId()) return;
  markUpdatePending();
}

/**
 * Wire up the lifecycle. Runs once on import; `swRegistration.ts` imports this
 * module so it is live for the whole page lifetime.
 */
function init(): void {
  if (typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    void onControllerChange();
  });

  startMinSupportedVersionPolling();
}

init();
